export type SchemaKind =
  | 'home'
  | 'service'
  | 'list'
  | 'contact'
  | 'company'
  | 'article-news'
  | 'article-blog'
  | 'none';

export interface RouteSeo {
  /** Route path or pattern (with :param). */
  path: string;
  match: 'exact' | 'pattern';
  /** Whether the route may be indexed by search engines. */
  indexable: boolean;
  /** Whether the route should appear in sitemap.xml. */
  sitemapInclude: boolean;
  title: string;
  description: string;
  keywords: string;
  ogType: string;
  schemaKind: SchemaKind;
  serviceName?: string;
}

/**
 * Authoritative route manifest for SEO. This is the source of truth for which
 * routes are public, indexable and sitemap-eligible. Private/auth/admin routes
 * are explicitly marked as non-indexable and are never included in the sitemap.
 */
export const ROUTE_SEO: RouteSeo[] = [
  {
    path: '/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: '株式会社ルヴィラ｜AIで会社の成長を止める滞りを改善',
    description:
      '株式会社ルヴィラは、AIを活用して会社の成長を妨げる業務・情報・判断の滞りを見つけ、改善します。AI業務改善、AIシステム開発、AI運用・改善支援、AI集客支援を全国オンラインで提供します。',
    keywords: '株式会社ルヴィラ,AI業務改善,AIシステム開発,AI運用・改善支援,AI集客支援,Luvira Inc.',
    ogType: 'website',
    schemaKind: 'home',
  },
  {
    path: '/services/ai-improvement/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: 'AI業務改善｜株式会社ルヴィラ',
    description:
      'まずは一つの業務から、AIで改善。LUVIRAは企業ごとの業務と課題を確認し、改善効果の高い業務からAIの活用を始めます。AIに詳しくない企業でも、業務の確認から伴走します。',
    keywords: 'AI業務改善,業務効率化,業務自動化,AI導入,株式会社ルヴィラ',
    ogType: 'website',
    schemaKind: 'service',
    serviceName: 'AI業務改善',
  },
  {
    path: '/services/system-development/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: 'AIシステム開発｜株式会社ルヴィラ',
    description:
      '業務に合うAIシステムを、一緒に考える。LUVIRAは業務の流れを確認し、必要な機能と運用方法を考えたうえで、AIを組み込んだシステムを設計・開発します。',
    keywords: 'AIシステム開発,業務システム開発,AI開発,システム開発,株式会社ルヴィラ',
    ogType: 'website',
    schemaKind: 'service',
    serviceName: 'AIシステム開発',
  },
  {
    path: '/services/ai-operation/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: 'AI運用・改善支援｜株式会社ルヴィラ',
    description:
      '導入したAIが現場で使われていない。LUVIRAは利用状況や現場の課題を確認し、運用方法を見直し、AIを業務で使い続けるための支援を行います。',
    keywords: 'AI運用,AI改善支援,AI定着,運用支援,株式会社ルヴィラ',
    ogType: 'website',
    schemaKind: 'service',
    serviceName: 'AI運用・改善支援',
  },
  {
    path: '/services/ai-marketing/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: 'AI集客支援｜株式会社ルヴィラ',
    description:
      '集客を増やす前に、顧客の課題と届ける価値を明確に。LUVIRAはAIを活用し、顧客理解・発信内容・施策実行・反応確認をつなげ、集客活動を事業の次の判断につなげます。',
    keywords: 'AI集客,AIマーケティング,集客支援,Web集客,株式会社ルヴィラ',
    ogType: 'website',
    schemaKind: 'service',
    serviceName: 'AI集客支援',
  },
  {
    path: '/services/ai-security/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: 'Luvira ASA｜AI利用リスク診断｜株式会社ルヴィラ',
    description:
      'Luvira ASAは、合意した範囲でAIの利用状況・情報・権限・外部連携を確認し、想定されるリスクと次に検討すべき対応を整理する診断サービスです。',
    keywords: 'Luvira ASA,AIセキュリティ,AIリスク診断,AIガバナンス,株式会社ルヴィラ',
    ogType: 'website',
    schemaKind: 'service',
    serviceName: 'Luvira ASA',
  },
  {
    path: '/news/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: 'ニュース｜株式会社ルヴィラ',
    description: '株式会社ルヴィラからのお知らせ・最新情報をお届けします。',
    keywords: 'ニュース,お知らせ,株式会社ルヴィラ',
    ogType: 'website',
    schemaKind: 'list',
  },
  {
    path: '/news/:slug/',
    match: 'pattern',
    indexable: true,
    sitemapInclude: false,
    title: 'ニュース｜株式会社ルヴィラ',
    description: '株式会社ルヴィラからのお知らせ・最新情報をお届けします。',
    keywords: 'ニュース,お知らせ,株式会社ルヴィラ',
    ogType: 'article',
    schemaKind: 'article-news',
  },
  {
    path: '/blog/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: 'ブログ｜株式会社ルヴィラ',
    description: 'AI活用に役立つナレッジやノウハウを発信しています。',
    keywords: 'ブログ,AI活用,ナレッジ,株式会社ルヴィラ',
    ogType: 'website',
    schemaKind: 'list',
  },
  {
    path: '/blog/:slug/',
    match: 'pattern',
    indexable: true,
    sitemapInclude: false,
    title: 'ブログ｜株式会社ルヴィラ',
    description: 'AI活用に役立つナレッジやノウハウを発信しています。',
    keywords: 'ブログ,AI活用,ナレッジ,株式会社ルヴィラ',
    ogType: 'article',
    schemaKind: 'article-blog',
  },
  {
    path: '/contact/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: 'お問い合わせ｜株式会社ルヴィラ',
    description:
      'サービスに関するご質問や見積もり依頼など、お気軽にお問い合わせください。AI業務改善・AIシステム開発・AI運用・改善支援・AI集客支援・Luvira ASAについてご相談いただけます。',
    keywords: 'お問い合わせ,無料相談,AI相談,株式会社ルヴィラ',
    ogType: 'website',
    schemaKind: 'contact',
  },
  {
    path: '/company/',
    match: 'exact',
    indexable: true,
    sitemapInclude: true,
    title: '会社情報｜株式会社ルヴィラ',
    description:
      '株式会社ルヴィラの会社概要と代表からのメッセージ。AIを活用して会社の成長を妨げる業務・情報・判断の滞りを見つけ、改善につなげるパートナーです。',
    keywords: '会社概要,株式会社ルヴィラ,Luvira Inc.,代表挨拶',
    ogType: 'website',
    schemaKind: 'company',
  },
  // Non-indexable routes (excluded from sitemap, marked noindex at runtime).
  {
    path: '/contact/thanks/',
    match: 'exact',
    indexable: false,
    sitemapInclude: false,
    title: '送信完了｜株式会社ルヴィラ',
    description: '',
    keywords: '',
    ogType: 'website',
    schemaKind: 'none',
  },
  {
    path: '/admin/login',
    match: 'exact',
    indexable: false,
    sitemapInclude: false,
    title: '管理ログイン｜株式会社ルヴィラ',
    description: '',
    keywords: '',
    ogType: 'website',
    schemaKind: 'none',
  },
  {
    path: '/admin',
    match: 'exact',
    indexable: false,
    sitemapInclude: false,
    title: '管理画面｜株式会社ルヴィラ',
    description: '',
    keywords: '',
    ogType: 'website',
    schemaKind: 'none',
  },
  {
    path: '/404',
    match: 'exact',
    indexable: false,
    sitemapInclude: false,
    title: 'ページが見つかりません｜株式会社ルヴィラ',
    description: '',
    keywords: '',
    ogType: 'website',
    schemaKind: 'none',
  },
];

export function normalizePath(path: string): string {
  if (!path) return '/';
  const trimmed = path.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

export function matchRoute(pathname: string): RouteSeo | null {
  const path = normalizePath(pathname);

  for (const route of ROUTE_SEO) {
    if (route.match === 'exact' && normalizePath(route.path) === path) {
      return route;
    }
  }

  for (const route of ROUTE_SEO) {
    if (route.match !== 'pattern') continue;
    const pattern = normalizePath(route.path).replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    if (regex.test(path)) return route;
  }

  return null;
}